import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Sprout, Store, Tag as TagIcon, X } from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { apiFetch } from "../../services/api";
import { useAuthedApi } from "../../hooks/useAuthedApi";
import LoadingState from "../../components/LoadingState";
import { T } from "../../constants/theme";

export default function PortfolioPage() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sellForm, setSellForm] = useState({});

  const { data: holdingsData, loading: holdingsLoading } = useAuthedApi(
    "/portfolio/holdings",
    token,
    [reloadKey],
  );
  const { data: listingsData, loading: listingsLoading } = useAuthedApi(
    "/market/listings",
    token,
    [reloadKey],
  );
  const holdings = holdingsData?.data || [];
  const myListings = (listingsData?.data || []).filter(
    (l) => user && l.seller_id === user.id,
  );

  const refresh = () => setReloadKey((k) => k + 1);

  const updateSellForm = (productId, field, value) =>
    setSellForm((m) => ({
      ...m,
      [productId]: { ...m[productId], [field]: value },
    }));

  const listForSale = async (holding) => {
    const draft = sellForm[holding.product_id] || {};
    const qty = Number(draft.qty || 0);
    const price = Number(draft.price || 0);
    if (!qty || qty <= 0 || qty > holding.qty) {
      toast("Enter a valid quantity you own", "error");
      return;
    }
    if (!price || price <= 0) {
      toast("Enter a valid price per share", "error");
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch(
        "/market/listings",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_id: holding.product_id,
            qty,
            price_per_share: price,
          }),
        },
        { authToken: token },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || body.error || "Unable to list shares");
      toast("Shares listed on the market");
      setSellForm((m) => ({ ...m, [holding.product_id]: {} }));
      refresh();
    } catch (e) {
      toast(e.message || "Failed to list shares", "error");
    } finally {
      setBusy(false);
    }
  };

  const cancelListing = async (listingId) => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/market/listings/${listingId}`,
        { method: "DELETE" },
        { authToken: token },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || body.error || "Unable to cancel listing");
      toast("Listing cancelled");
      refresh();
    } catch (e) {
      toast(e.message || "Failed to cancel listing", "error");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    padding: "12px",
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: T.paper,
    color: T.ink,
    outline: "none",
    width: 110,
  };

  const sectionHeader = (icon, title) => (
    <h3 style={{ color: T.ink, display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
      {icon}
      {title}
    </h3>
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 80px" }}>
      <style>{`
        .portfolio-input:focus { border-color: ${T.gold} !important; box-shadow: 0 0 0 3px ${T.goldLight}; }
        .portfolio-card { transition: border-color .2s ease, box-shadow .2s ease; }
        .portfolio-card:hover { border-color: rgba(95,203,140,0.35) !important; box-shadow: ${T.shadowSoft}; }
        .portfolio-sell:hover:not(:disabled) { transform: translateY(-1px); box-shadow: ${T.shadowMedium}; }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: T.goldLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Wallet size={24} color={T.gold} />
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", color: T.ink, margin: 0 }}>
          My Portfolio
        </h1>
      </div>
      <p style={{ color: T.muted, marginBottom: 32 }}>
        Shares you hold across projects. List some for resale, or browse{" "}
        <button
          onClick={() => navigate("/market")}
          style={{ border: "none", background: "none", color: T.gold, fontWeight: 700, cursor: "pointer" }}
        >
          the secondary market.
        </button>
      </p>

      {sectionHeader(<Sprout size={18} color={T.gold} />, "Holdings")}
      {holdingsLoading ? (
        <LoadingState />
      ) : holdings.length === 0 ? (
        <div
          style={{
            color: T.muted,
            border: `1px dashed ${T.border}`,
            borderRadius: 16,
            padding: 32,
            textAlign: "center",
            marginBottom: 40,
          }}
        >
          You don't hold any shares yet. Invest in a project from the shop to
          get started.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16, marginBottom: 48 }}>
          {holdings.map((h) => (
            <div
              key={h.id}
              className="portfolio-card"
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 16,
                padding: 20,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
                boxShadow: T.shadowSoft,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 14,
                    overflow: "hidden",
                    background: T.paper,
                    border: `1px solid ${T.border}`,
                    boxShadow: T.shadowSoft,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {h.product_image_url ? (
                    <img
                      src={h.product_image_url}
                      alt={h.product_name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Sprout size={22} color={T.muted} />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: T.ink }}>{h.product_name}</div>
                  <div style={{ fontSize: 13, color: T.muted }}>
                    <span style={{ color: T.gold, fontWeight: 700 }}>{h.qty}</span> shares held ·
                    current price LKR {Number(h.product_price || 0).toFixed(2)}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  className="portfolio-input"
                  type="number"
                  min={1}
                  max={h.qty}
                  placeholder="Qty"
                  style={inputStyle}
                  value={sellForm[h.product_id]?.qty || ""}
                  onChange={(e) => updateSellForm(h.product_id, "qty", e.target.value)}
                />
                <input
                  className="portfolio-input"
                  type="number"
                  min={0.01}
                  step="0.01"
                  placeholder="Price/share"
                  style={inputStyle}
                  value={sellForm[h.product_id]?.price || ""}
                  onChange={(e) => updateSellForm(h.product_id, "price", e.target.value)}
                />
                <button
                  className="portfolio-sell"
                  onClick={() => listForSale(h)}
                  disabled={busy}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: `linear-gradient(135deg, ${T.gold}, ${T.goldHover})`,
                    color: T.white,
                    border: "none",
                    borderRadius: 10,
                    padding: "12px 20px",
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "transform .2s ease, box-shadow .2s ease",
                  }}
                >
                  <TagIcon size={14} />
                  Sell
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {sectionHeader(<Store size={18} color={T.gold} />, "My Listings")}
      {listingsLoading ? (
        <LoadingState />
      ) : myListings.length === 0 ? (
        <div
          style={{
            color: T.muted,
            border: `1px dashed ${T.border}`,
            borderRadius: 16,
            padding: 32,
            textAlign: "center",
          }}
        >
          You have no active listings.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {myListings.map((l) => (
            <div
              key={l.id}
              className="portfolio-card"
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 16,
                padding: 20,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
                boxShadow: T.shadowSoft,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: T.ink }}>{l.product_name}</div>
                <div style={{ fontSize: 13, color: T.muted }}>
                  {l.qty_remaining} of {l.qty_total} shares left at LKR{" "}
                  {Number(l.price_per_share).toFixed(2)}/share
                </div>
              </div>
              <button
                onClick={() => cancelListing(l.id)}
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "transparent",
                  color: "#e07a5f",
                  border: "1px solid #e07a5f",
                  borderRadius: 10,
                  padding: "10px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <X size={14} />
                Cancel Listing
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
